/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/
import { Inject, Injectable } from '@angular/core';

import Shepherd from 'shepherd.js';
import { getTourStepData, hostName } from './tour-steps';
import tourTemplate from './tour-step.html';

/**
 * Service to create tour specific steps object.
 */
@Injectable({
  providedIn: 'root',
})
export class TourDetailsService {
  /**
   * Template for the tour step body.
   */
  tourTemplate = null;

  /**
   * Tour step modal selector class to handle dimensions and animations.
   */
  elementSelectorClass: string = '.tippy-tooltip';

  /**
   * Delay in ms between different tour steps.
   */
  static DELAY_BETWEEN_TOUR_STEP = 600;

  /**
   * Delay in ms for animation for the step.
   */
  static ANIMATION_DELAY = 500;

  /**
   * Unique identifier for Groups anchor for mobile
   */
  mobileAnchorIdentifier = 'bulb-group-tab-tour-id-mobile';

  /**
   * Unique identifier for share button tour toolbar
   */
  shareButton = 'share-button';

  /**
   * Unique identifier for Groups anchor for Web
   */
  webAnchorIdentifier = 'groups-tab';

  /**
   * Ids/classname for candidate element for adding additional class.
   */
  targetCandidates = ['share-button-text', 'publish-page', 'page-ellipsis'];

  constructor(@Inject('$translate') private $translate: any) {
  }

  /**
   * Handle step in/out animation.
   * @param animationClass - The animation class to be applied.
   */
  animateStep(animationClass) {
    const contentDiv = document.querySelector(this.elementSelectorClass);
    contentDiv.removeAttribute('data-animation');
    contentDiv.classList.add('tour-animate', animationClass);
  }

  /**
   * Set the dimension for modal.
   */
  setModalDimension() {
    const element = document.querySelector(this.elementSelectorClass);
    element.removeAttribute('style');
    element.classList.add('shepherd-modal-dimension');
  }

  /**
   * handle the animation delay between the steps.
   */
  handleDelay = (isMobile = false, isGroupStep = false) => {
    this.setAnchorId(isMobile, isGroupStep);
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, TourDetailsService.DELAY_BETWEEN_TOUR_STEP);
    });
  };

  /**
   * Default options for all the tour steps.
   */
  defaultStepOptions = {
    cancelIcon: {
      enabled: true,
    },
    modal: {
      clickOutsideToClose: false,
    },
    tippyOptions: {
      duration: TourDetailsService.ANIMATION_DELAY,
      onShow: (instance) => {
        this.toggleExtraClassOnSection(instance, true);
      },
      onHide: (instance) => {
        this.toggleExtraClassOnSection(instance, false);
        if (instance.reference.id === this.mobileAnchorIdentifier) {
          const node = document.getElementById(instance.reference.id);
          if (node && node.parentNode) {
            (node.parentNode as HTMLElement).classList.remove('group-menu');
            node.parentNode.removeChild(node);
          }
        }
      },
    },
    highlightClass: 'highlight-target',
  };

  /**
   * Buttons to be used in the steps.
   * @param tourObject Shephered tour object
   */
  getButtons = (tourObject: Shepherd.Tour) => ({
    next: {
      classes: 'bulb-onboarding-next-button',
      text: this.translateText('onboarding_tour_next_button'),
      action: () => {
        tourObject.next();
      },
    },
    done: {
      classes: 'bulb-done-button',
      text: this.translateText('onboarding_tour_done_button'),
      action: () => {
        tourObject.getCurrentStep().hide();
        setTimeout(() => {
          tourObject.complete();
        }, TourDetailsService.DELAY_BETWEEN_TOUR_STEP);
      },
    },
    start: {
      classes: 'next-button bulb-onboarding-start-button',
      text: this.translateText('onboarding_tour_get_started_button'),
      action: () => tourObject.next(),
    },
    skip: {
      classes: 'next-button bulb-onboarding-skip-button',
      text: this.translateText('button_skip'),
      action: () => tourObject.cancel(),
    },
  });

  /**
   * Create list of steps for main tour.
   * @param tourObject Shephered tour object
   */
  createMainTourSteps(tourObject: Shepherd.Tour) {
    return [
      {
        buttons: [
          this.getButtons(tourObject).skip,
          this.getButtons(tourObject).start,
        ],
        classes: 'bulb-onboarding-tour-modal centered-tour',
        id: 'welcomeStep',
        title: this.translateText('onboarding_tour_get_started_title'),
        text: this.transformStepTemplate(getTourStepData('welcomeStep')),
        when: {
          hide: () => this.animateStep('tour-bounce-out-down'),
        },
      },
      {
        beforeShowPromise: this.handleDelay,
        buttons: [
          this.getButtons(tourObject).next,
        ],
        canClickTarget: false,
        classes: 'bulb-onboarding-tour-modal',
        id: 'createPageStep',
        title: this.translateText('onboarding_tour_create_page_title'),
        text: this.transformStepTemplate(getTourStepData('createPageStep')),
        when: {
          show: () => this.animateStep('tour-bounce-in-up'),
          hide: () => this.animateStep('tour-bounce-out-down'),
        },
      },
      {
        beforeShowPromise: this.handleDelay,
        buttons: [
          this.getButtons(tourObject).next,
        ],
        canClickTarget: false,
        classes: 'bulb-onboarding-tour-modal',
        id: 'createCollectionStep',
        title: this.translateText('onboarding_tour_create_collection_title'),
        text: this.transformStepTemplate(getTourStepData('createCollectionStep')),
        when: {
          show: () => this.animateStep('tour-bounce-in-up'),
          hide: () => this.animateStep('tour-bounce-out-down'),
        },
      },
    ];
  }

  /**
   * Create list of steps for menu tour.
   * @param isMobile boolean flag true for mobile
   * @param tourObject Shephered tour object
   */
  createMenuTourSteps(isMobile: boolean, tourObject: Shepherd.Tour) {
    return [
      {
        attachTo: {
          element: isMobile ? '#asset-library-mobile' : '#asset-library',
          on: 'bottom',
        },
        buttons: [
          this.getButtons(tourObject).next,
        ],
        canClickTarget: false,
        classes: 'bulb-onboarding-tour-modal',
        highlightClass: isMobile ? 'highlight-target-mobile' : 'highlight-target',
        id: 'assetLibraryStep',
        title: this.translateText('onboarding_tour_asset_library_title'),
        text: this.transformStepTemplate(getTourStepData('assetLibraryStep')),
        when: {
          hide: () => this.animateStep('tour-bounce-out-up'),
        },
      },
      {
        beforeShowPromise: () => this.handleDelay(isMobile, true),
        attachTo: {
          element: isMobile ? `#${this.mobileAnchorIdentifier}` : `#${this.webAnchorIdentifier}`,
          on: 'bottom',
        },
        buttons: [
          this.getButtons(tourObject).next,
        ],
        canClickTarget: false,
        classes: 'bulb-onboarding-tour-modal',
        id: 'groupStep',
        highlightClass: isMobile ? 'highlight-target-mobile' : 'highlight-target',
        title: this.translateText('onboarding_tour_group_title'),
        text: this.transformStepTemplate(getTourStepData('groupStep')),
        when: {
          show: () => {
            window.menuTour('GROUPS');
            this.animateStep('tour-bounce-in-down');
          },
        },
      },
    ];
  }

  /**
   * Create list of steps for page intro tour.
   * @param isMobile boolean flag true for mobile
   * @param tourObject Shephered tour object
   * @param ckeEnabled flag for checking, if ckeditor is enabled.
   */
  createPageIntroSteps(isMobile: boolean, tourObject: Shepherd.Tour, ckeEnabled: boolean = false) {
    return ckeEnabled ? this.createPageTourForCKE(tourObject) : this.createPageTourForMedium(tourObject, isMobile);
  }

  /**
   * Create list of steps for page tour.
   * @param isMobileDevice boolean flag true for mobile
   * @param isMediumDevice boolean flag true for medium-up devices
   * @param tourObject Shephered tour object
   */
  createPageTourSteps(isMobileDevice: boolean, isMediumDevice: boolean, tourObject: Shepherd.Tour) {
    return [
      {
        beforeShowPromise: this.handleDelay,
        attachTo: {
          element: `.${this.shareButton}`,
          on: 'bottom',
        },
        buttons: [
          this.getButtons(tourObject).next,
        ],
        canClickTarget: false,
        classes: 'bulb-onboarding-tour-modal',
        id: 'shareStep',
        title: this.translateText('onboarding_tour_share_title'),
        text: this.transformStepTemplate(getTourStepData('shareStep')),
        when: {
          show: () => this.animateStep('tour-bounce-in-down'),
          hide: () => this.animateStep('tour-bounce-out-up'),
        },
      },
      {
        beforeShowPromise: this.handleDelay,
        attachTo: {
          element: isMediumDevice ? '.bulb-footer-comments-counter' : '#comment-textarea',
          on: 'top',
        },
        buttons: [
          this.getButtons(tourObject).next,
        ],
        classes: 'bulb-onboarding-tour-modal',
        highlightClass: 'highlight-comment-step',
        id: 'commentsStep',
        title: this.translateText('onboarding_tour_comment_title'),
        text: this.transformStepTemplate(getTourStepData('commentsStep')),
        when: {
          show: () => this.animateStep('tour-bounce-in-up'),
          hide: () => this.animateStep('tour-bounce-out-down'),
        },
      },
    ];
  }

  /**
   * Create Go get em step.
   * @param tourObject Shephered tour object
   */
  createTourCompletedStep(firstName: string, tourObject: Shepherd.Tour) {
    return {
      beforeShowPromise: this.handleDelay,
      buttons: [
        this.getButtons(tourObject).done,
      ],
      id: 'doneStep',
      title: this.translateText('onboarding_tour_done_title', { name: firstName }),
      classes: 'bulb-onboarding-congrats-modal',
      text: this.transformStepTemplate(getTourStepData('doneStep')),
      when: {
        show: () => this.animateStep('tour-bounce-in-up'),
        hide: () => this.animateStep('tour-bounce-out-down'),
      },
    };
  }

  /**
   * Transforms the step template with step data
   * @param stepData metadata for the step
   */
  transformStepTemplate(stepData: any) {
    if (this.tourTemplate === null) {
      this.tourTemplate = tourTemplate;
    }
    let transformedTemplate: string = this.tourTemplate;

    if (stepData.imageSource) {
      let imageClass = 'no-class';
      if (stepData.stepName === 'welcomeStep') {
        imageClass = 'bulb-tour-logo-height';
      } else if (stepData.stepName === 'doneStep') {
        imageClass = 'bulb-end-tour-logo-height';
      }
      transformedTemplate = transformedTemplate.replace(
        'image_here',
        `<img class=${imageClass} src=${stepData.imageSource}>`,
      );
    } else {
      transformedTemplate = transformedTemplate.replace('image_here', '');
    }

    if (stepData.bodyText) {
      let translatedText;
      if (stepData.stepName === 'doneStep') {
        translatedText = this.translateText(
          stepData.bodyText,
          { link: `<a id="bulbAnchorTag" href="https://${hostName}/library" target="_blank">bulbLibrary</a>` },
        );
      } else {
        translatedText = this.translateText(stepData.bodyText);
      }
      transformedTemplate = transformedTemplate.replace('step_body_here', translatedText);
    } else {
      transformedTemplate = transformedTemplate.replace('step_body_here', '');
    }

    if (stepData.anchorText && stepData.anchorLink && stepData.additionalText) {
      transformedTemplate = transformedTemplate.replace(
        'anchor_here',
        `<a id='bulbAnchorTag' href=${stepData.anchorLink} target='_blank'>
        ${this.translateText(stepData.anchorText)}</a> ${this.translateText(stepData.additionalText)}`,
      );
    } else if (stepData.anchorText && stepData.anchorLink) {
      transformedTemplate = transformedTemplate.replace(
        'anchor_here',
        `<a
          id='bulbAnchorTag'
          href=${stepData.anchorLink}
          target='_blank'
        >${this.translateText(stepData.anchorText)}</a>`,
      );
    } else {
      transformedTemplate = transformedTemplate.replace('anchor_here', '');
    }

    if (!stepData.showCarousel) {
      transformedTemplate = transformedTemplate.replace('carousel_class_here', 'bulb-tour-carousel');
    } else {
      transformedTemplate = transformedTemplate.replace(stepData.stepName, 'active-step');
    }

    return transformedTemplate;
  }

  /**
   * Translates the key and returns the translated text.
   * @param key translate key
   * @param args optional object to replace content in translations
   */
  translateText(key: string, args?: object) {
    return this.$translate.instant(key, args);
  }

  /**
   * Method to set id dynamically for group tab/accordion.
   * @param isMobile - true for mobile devices.
   * @param isGroupStep - true if group step is active.
   */
  setAnchorId = (isMobile, isGroupStep) => {
    // do not process if we are not dealing with group step.
    if (!isGroupStep) return;
    const selector = isMobile ? '.accordion' : '.tabs';
    const tabs = document.querySelector(selector);
    if (tabs) {
      let elementFound = false;
      const tabItems = tabs.children;
      const node = document.getElementById(this.mobileAnchorIdentifier);
      for (let i = 0; i < tabItems.length; i += 1) {
        const tabId = tabItems[i].getAttribute('id');
        if (tabId === 'groups-mobile') {
          if (!tabItems[i].contains(node)) {
            tabItems[i].classList.add('group-menu');
            const htmlDivElement = document.createElement('div');
            htmlDivElement.setAttribute('class', 'group-anchor-tag');
            htmlDivElement.setAttribute('id', this.mobileAnchorIdentifier);
            tabItems[i].insertBefore(htmlDivElement, tabItems[i].firstChild);
          }
          elementFound = true;
        } else if (tabId === 'groups') {
          tabItems[i].children[0].setAttribute('id', this.webAnchorIdentifier);
          elementFound = true;
        }
        if (elementFound) break;
      }
    }
  };

  /**
   * Method to add/remove additional class on selected section.
   * @param instance - current tour step.
   * @param addClass - true, if we want to add class, false otherwise.
   */
  toggleExtraClassOnSection = (instance, addClass) => {
    let targetElement;
    let className;
    if (this.targetCandidates.indexOf(instance.reference.id) > -1
      || instance.reference.classList.contains(this.targetCandidates[0])) {
      targetElement = document.querySelector('.bulb-page-header');
      className = 'bulb-override-page-header';
    }
    if (instance.reference.id === 'asset-library-mobile' || instance.reference.id === this.mobileAnchorIdentifier) {
      targetElement = document.querySelector('#mobileTabContainer');
      className = 'bulb-override-mobile-menu';
    }
    if (targetElement) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      targetElement && !addClass ? targetElement.classList.remove(className)
        : targetElement.classList.add(className);
    }
  };

  /**
   * Create page intro tour for CKEditor.
   * @param tourObject Shephered tour object
   */
  createPageTourForCKE = (tourObject: Shepherd.Tour) => [
    {
      buttons: [
        this.getButtons(tourObject).next,
      ],
      attachTo: {
        element: '.ck-media-dropdown',
        on: 'top',
      },
      classes: 'bulb-onboarding-tour-modal',
      id: 'pageEditStepCKE',
      title: this.translateText('onboarding_tour_cke_heading'),
      text: this.transformStepTemplate(getTourStepData('pageEditStepCKE')),
      when: {
        show: () => this.animateStep('tour-bounce-in-down'),
        hide: () => this.animateStep('tour-bounce-out-up'),
      },
    },
  ];

  /**
   * Create page intro tour for Medium Editor.
   * @param tourObject Shephered tour object
   * @param isMobile true for mobile devices.
   */
  createPageTourForMedium = (tourObject: Shepherd.Tour, isMobile: boolean) => [
    {
      buttons: [
        this.getButtons(tourObject).next,
      ],
      classes: 'bulb-onboarding-menu-tour',
      id: 'pageEditStep',
      title: this.translateText('onboarding_tour_page_intro1_title'),
      text: isMobile ? this.transformStepTemplate(getTourStepData('pageEditMobileStep'))
        : this.transformStepTemplate(getTourStepData('pageEditStep')),
      when: {
        show: () => this.setModalDimension(),
      },
    },
    {
      beforeShowPromise: this.handleDelay,
      buttons: [
        this.getButtons(tourObject).next,
      ],
      classes: 'bulb-onboarding-menu-tour bulb-page-intro-media',
      id: 'pageMediaStep',
      title: this.translateText(isMobile
        ? 'onboarding_tour_page_intro2_title_mobile'
        : 'onboarding_tour_page_intro2_title'),
      text: isMobile ? this.transformStepTemplate(getTourStepData('pageMediaMobileStep'))
        : this.transformStepTemplate(getTourStepData('pageMediaStep')),
      when: {
        show: () => this.setModalDimension(),
        hide: () => this.animateStep('tour-bounce-out-down'),
      },
    },
  ];
}
